/**
 * VUKZ Rankings Service
 * Calls /api/vukz/* endpoints for Performance OS ranking data
 */
import { AZURE } from './api';

/**
 * Get rankings list with scope filtering
 * @param {Object} params
 * @param {number} params.year - Competition year (required)
 * @param {string} params.event_key - Format: {distance}_{stroke}_{gender}_{course} (required)
 * @param {string} params.scope - top20|vukz|br|usa|aus (required)
 * @param {boolean} params.coach_only - Filter to coach's athletes only
 * @param {number} params.limit - Max results (default 100)
 * @param {number} params.offset - Pagination offset
 * @returns {Promise<Object>} { success, items, meta }
 */
export const getVukzRankings = async (params = {}) => {
  try {
    const qs = new URLSearchParams();
    if (params.year) qs.append('year', params.year);
    if (params.event_key) qs.append('event_key', params.event_key);
    if (params.scope) qs.append('scope', params.scope);
    if (params.coach_only) qs.append('coach_only', 'true');
    if (params.limit) qs.append('limit', params.limit);
    if (params.offset) qs.append('offset', params.offset);
    const { data } = await AZURE.get(`/vukz/rankings?${qs.toString()}`);
    return data;
  } catch (error) {
    console.error('[vukzRankings] Error fetching rankings:', error);
    return { success: false, items: [], error: error.message };
  }
};

/**
 * Get athlete ranking summary — all events with multi-scope ranks
 * @param {number} athleteId - Swimmer ID
 * @param {number} year - Competition year
 * @returns {Promise<Object>} { success, athlete_id, year, items: [{ event_key, event_label, best_seconds, best_value, rank_vukz, rank_br, rank_usa, rank_aus }] }
 */
export const getAthleteRankingSummary = async (athleteId, year) => {
  try {
    const { data } = await AZURE.get(`/vukz/athletes/${athleteId}/ranking-summary?year=${year}`);
    return data;
  } catch (error) {
    console.error('[vukzRankings] Error fetching athlete summary:', error);
    return { success: false, items: [], error: error.message };
  }
};

/**
 * Get ranking badges for all coach's athletes in a given event
 * @param {number} year - Competition year
 * @param {string} event_key - Event key format
 * @returns {Promise<Object>} { success, items: [{ athlete_id, athlete_name, rank_vukz, rank_br, best_value }] }
 */
export const getCoachRankingBadges = async (year, event_key) => {
  try {
    const { data } = await AZURE.get(`/vukz/coach/my-athletes/ranking-badges?year=${year}&event_key=${event_key}`);
    return data;
  } catch (error) {
    console.error('[vukzRankings] Error fetching coach badges:', error);
    return { success: false, items: [], error: error.message };
  }
};

/**
 * Get athlete's own swimming results (from /api/my/swimming/results)
 * @param {Object} params - { from, to, limit }
 * @returns {Promise<Object>} { success, athlete, results }
 */
export const getMySwimmingResults = async (params = {}) => {
  try {
    const qs = new URLSearchParams();
    if (params.from) qs.append('from', params.from);
    if (params.to) qs.append('to', params.to);
    if (params.limit) qs.append('limit', params.limit);
    const url = qs.toString() ? `/my/swimming/results?${qs}` : '/my/swimming/results';
    const { data } = await AZURE.get(url);
    return data;
  } catch (error) {
    console.error('[vukzRankings] Error fetching my results:', error);
    return { success: false, results: [], error: error.message };
  }
};

/**
 * Get coach's accessible athletes (from /api/coach/athletes)
 * @returns {Promise<Object>} { success, athletes }
 */
export const getCoachAthletes = async () => {
  try {
    const { data } = await AZURE.get('/coach/athletes');
    return data;
  } catch (error) {
    console.error('[vukzRankings] Error fetching coach athletes:', error);
    return { success: false, athletes: [], error: error.message };
  }
};

// ─── Helpers ──────────────────────────────────────────────────

/**
 * Build event_key from components
 * @param {number} distance - e.g. 100
 * @param {string} stroke - e.g. 'Livre'
 * @param {string} gender - 'M' or 'F'
 * @param {string} course - 'LCM' or 'SCM'
 * @returns {string} e.g. '100_free_m_lcm'
 */
export const buildEventKey = (distance, stroke, gender = 'M', course = 'LCM') => {
  const strokeMap = {
    'Livre': 'free',
    'Costas': 'back',
    'Peito': 'breast',
    'Borboleta': 'fly',
    'Medley': 'im',
  };
  const strokeKey = strokeMap[stroke] || stroke.toLowerCase();
  return `${distance}_${strokeKey}_${gender.toLowerCase()}_${course.toLowerCase()}`;
};

/**
 * Parse event_key to display label
 * @param {string} eventKey - e.g. '100_free_m_lcm'
 * @returns {string} e.g. '100m Livre'
 */
export const parseEventLabel = (eventKey) => {
  if (!eventKey) return '';
  const parts = eventKey.split('_');
  const distance = parts[0];
  const strokeMap = {
    'free': 'Livre',
    'back': 'Costas',
    'breast': 'Peito',
    'fly': 'Borboleta',
    'im': 'Medley',
  };
  const stroke = strokeMap[parts[1]] || parts[1];
  return `${distance}m ${stroke}`;
};

export default {
  getVukzRankings,
  getAthleteRankingSummary,
  getCoachRankingBadges,
  getMySwimmingResults,
  getCoachAthletes,
  buildEventKey,
  parseEventLabel,
};
